import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import {
  API_ciudadanos,
  API_servicios,
  API_BASE_URL,
} from "../../config/apiConfig";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";

const ModalAgendarCita = ({
  isOpen,
  onClose,
  onCitaCreada,
  dniPorDefecto = "",
  usuarioPorDefecto = null,
}) => {
  const { user: technician, token } = useAuth();

  // Estados del formulario
  const [newEvent, setNewEvent] = useState({
    title: "",
    user: dniPorDefecto,
    date: "",
    startTime: "",
    endTime: "",
    service: "",
    modality: "",
  });

  // Estados para autocompletado por DNI
  const [matchedUsers, setMatchedUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);

  // Estados para servicios
  const [servicios, setServicios] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(false);

  // Resetear formulario cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (usuarioPorDefecto) {
        setNewEvent({
          title: `Cita con ${usuarioPorDefecto.name} ${usuarioPorDefecto.lastName}`,
          user: usuarioPorDefecto.dni || dniPorDefecto,
          date: "",
          startTime: "",
          endTime: "",
          service: "",
          modality: "",
        });
        setSelectedUserId(usuarioPorDefecto.userId);
        setSelectedUserData(usuarioPorDefecto);
      } else {
        setNewEvent({
          title: "",
          user: dniPorDefecto,
          date: "",
          startTime: "",
          endTime: "",
          service: "",
          modality: "",
        });
        setSelectedUserId(null);
        setSelectedUserData(null);
      }
      setMatchedUsers([]);
    }
  }, [isOpen, dniPorDefecto, usuarioPorDefecto]);

  // Buscar coincidencias de DNI
  useEffect(() => {
    const fetchCoincidencias = async () => {
      if (!newEvent.user || newEvent.user.trim().length < 3 || selectedUserId) {
        setMatchedUsers([]);
        return;
      }

      try {
        setSearching(true);
        const res = await fetch(API_ciudadanos, {
          headers: {
            "x-auth-token": token,
          },
        });
        const data = await res.json();

        const coincidencias = data.filter((c) =>
          c.dni?.toLowerCase().includes(newEvent.user.trim().toLowerCase())
        );

        setMatchedUsers(coincidencias);
      } catch (error) {
        console.error("Error al buscar coincidencias de DNI:", error);
      } finally {
        setSearching(false);
      }
    };

    fetchCoincidencias();
  }, [newEvent.user, selectedUserId]);

  // Seleccionar usuario de las coincidencias
  const handleSelectUsuario = (dni, nombre, id) => {
    const userData = matchedUsers.find((user) => user.userId === id);
    setNewEvent((prev) => ({
      ...prev,
      user: "",
      title: `Cita con ${nombre}`,
    }));
    setSelectedUserId(id);
    setSelectedUserData(userData);
    setMatchedUsers([]);
  };

  // Limpiar usuario seleccionado
  const handleClearSelectedUser = () => {
    setSelectedUserId(null);
    setSelectedUserData(null);
    setNewEvent((prev) => ({
      ...prev,
      user: "",
      title: "",
    }));
  };

  // Cargar servicios al montar el componente
  useEffect(() => {
    const fetchServicios = async () => {
      try {
        setLoadingServicios(true);
        const res = await fetch(API_servicios, {
          headers: {
            "x-auth-token": token,
          },
        });
        const data = await res.json();
        setServicios(data);
      } catch (error) {
        console.error("Error al cargar servicios:", error);
        toast.error("Error al cargar los servicios", {
          position: "top-right",
          autoClose: 2500,
        });
      } finally {
        setLoadingServicios(false);
      }
    };

    fetchServicios();
  }, []);

  // Crear la cita
  const handleAddEvent = async () => {
    const { title, user, date, startTime, endTime, service, modality } =
      newEvent;

    // Validación de campos obligatorios (según backend: subject, dni, name, email, phone, service + date/time del frontend)
    if (
      !title ||
      (!selectedUserData && !user) ||
      !date ||
      !startTime ||
      !endTime ||
      !service
    ) {
      if (!title) {
        toast.error("Debe introducir un asunto", {
          position: "top-right",
          autoClose: 2500,
        });
      } else if (!selectedUserData && !user) {
        toast.error("Debe seleccionar un usuario", {
          position: "top-right",
          autoClose: 2500,
        });
      } else if (!date) {
        toast.error("Debe introducir una fecha", {
          position: "top-right",
          autoClose: 2500,
        });
      } else if (!service) {
        toast.error("Debe introducir un tipo de servicio", {
          position: "top-right",
          autoClose: 2500,
        });
      } else if (!startTime) {
        toast.error("Debe introducir una hora de inicio", {
          position: "top-right",
          autoClose: 2500,
        });
      } else if (!endTime) {
        toast.error("Debe introducir una hora de fin", {
          position: "top-right",
          autoClose: 2500,
        });
      }
      return;
    }

    const start = new Date(`${newEvent.date}T${newEvent.startTime}`);
    const end = new Date(`${newEvent.date}T${newEvent.endTime}`);

    // Verificar si el usuario con el DNI está disponible
    let selectedUser;
    if (usuarioPorDefecto) {
      selectedUser = usuarioPorDefecto;
    } else if (selectedUserData) {
      selectedUser = selectedUserData;
    } else {
      toast.error("Usuario no encontrado", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    // Construir el cuerpo de la solicitud
    const body = {
      serviciosSociales: service === "Servicios Sociales" ? true : null,
      userId: selectedUser.userId,
      subject: title,
      technicianId: technician?.id,
      appointmentDate: start.toISOString(),
      state: "Aceptado",
      name: `${selectedUser.name} ${selectedUser.lastName}`,
      email: selectedUser.email,
      phone: selectedUser.phone,
      address: selectedUser.address,
      service: service === "Servicios Sociales" ? null : service,
      dni: selectedUserData ? selectedUserData.dni : newEvent.user,
      modality: modality,
    };

    try {
      const res = await fetch(
        `${API_ciudadanos}/${selectedUser.userId}/citas`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (res.ok) {
        const citaData = await res.json();
        const appointmentId = citaData.id;

        if (appointmentId) {
          // Segundo fetch para aceptar la cita
          try {
            const aceptarRes = await fetch(
              `${API_BASE_URL}/citas/${appointmentId}/aceptar`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  appointmentDate: start.toISOString(),
                  technicianId: technician?.id,
                  modality: modality,
                }),
              }
            );

            if (aceptarRes.ok) {
              const citaCreada = {
                title: newEvent.title,
                user: newEvent.user,
                start,
                end,
              };

              toast.success("Cita agendada", {
                position: "top-right",
                autoClose: 2500,
              });

              // Llamar callback para actualizar el componente padre
              if (onCitaCreada) {
                onCitaCreada(citaCreada);
              }

              onClose();
            } else {
              toast.error("Error al confirmar la cita", {
                position: "top-right",
                autoClose: 2500,
              });
            }
          } catch (aceptarError) {
            console.error("Error al aceptar la cita:", aceptarError);
            toast.error("Error al confirmar la cita", {
              position: "top-right",
              autoClose: 2500,
            });
          }
        } else {
          toast.error("Error: No se recibió el ID de la cita", {
            position: "top-right",
            autoClose: 2500,
          });
        }
      } else {
        toast.error("Error al agendar la cita", {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (error) {
      console.error("Error al agendar la cita:", error);
      toast.error("Error al agendar la cita", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="modal"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999998,
        margin: 0,
        padding: "20px",
        boxSizing: "border-box",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-content"
        style={{
          background: "white",
          padding: "2rem",
          borderRadius: "10px",
          minWidth: "300px",
          maxWidth: "500px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          margin: 0,
        }}
      >
        <h3
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "25px",
          }}
        >
          Nueva cita
        </h3>

        <div
          className="campo"
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            DNI del usuario: *
          </label>

          {selectedUserData ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                border: "1px solid #ddd",
                width: "fit-content",
              }}
            >
              <span>
                {selectedUserData.dni} - {selectedUserData.name}{" "}
                {selectedUserData.lastName}
              </span>
              <button
                type="button"
                onClick={handleClearSelectedUser}
                style={{
                  background: "none",
                  border: "none",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "16px",
                  padding: "0 4px",
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <input
              type="text"
              placeholder="Escribe DNI/NIE del usuario"
              value={newEvent.user}
              onChange={(e) => {
                setNewEvent({ ...newEvent, user: e.target.value });
              }}
              disabled={!!usuarioPorDefecto}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            />
          )}
        </div>

        {searching && (
          <div
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              textAlign: "center",
              color: "#666",
              marginBottom: "20px",
              marginTop: "-10px",
            }}
          >
            Buscando coincidencias...
          </div>
        )}

        {matchedUsers.length > 0 && !selectedUserData && (
          <ul
            className="sugerencias-dni"
            style={{ marginTop: "-10px", marginBottom: "20px" }}
          >
            {matchedUsers.map((user) => (
              <li
                key={user.userId}
                onClick={() =>
                  handleSelectUsuario(
                    user.dni,
                    `${user.name} ${user.lastName}`,
                    user.userId
                  )
                }
                className="pointer"
              >
                {user.dni} - {user.name} {user.lastName}
              </li>
            ))}
          </ul>
        )}

        <div
          className="campo"
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            Servicio: *
          </label>
          <select
            value={newEvent.service}
            onChange={(e) =>
              setNewEvent({ ...newEvent, service: e.target.value })
            }
            disabled={loadingServicios}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <option value="">
              {loadingServicios
                ? "Cargando servicios..."
                : "Seleccionar servicio"}
            </option>
            {servicios.map((servicio) => (
              <option key={servicio.nombre} value={servicio.nombre}>
                {servicio.nombre}
              </option>
            ))}
          </select>
        </div>

        <div
          className="campo"
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            Modalidad:
          </label>
          <select
            value={newEvent.modality}
            onChange={(e) =>
              setNewEvent({ ...newEvent, modality: e.target.value })
            }
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <option value="">Seleccionar modalidad</option>
            <option value="Presencial">Presencial</option>
            <option value="Virtual">Virtual</option>
            <option value="Telefónica">Telefónica</option>
          </select>
        </div>

        <div
          className="campo"
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            Asunto: *
          </label>
          <textarea
            placeholder="Describe brevemente el motivo de la cita"
            value={newEvent.title}
            onChange={(e) =>
              setNewEvent({ ...newEvent, title: e.target.value })
            }
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              minHeight: "80px",
              resize: "vertical",
            }}
          />
        </div>

        <div className="flex gap-30">
          <div className="column">
            <label
              style={{
                fontWeight: "bold",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Fecha: *
            </label>
            <input
              type="date"
              onChange={(e) =>
                setNewEvent({ ...newEvent, date: e.target.value })
              }
            />
          </div>
          <div className="column">
            <label
              style={{
                fontWeight: "bold",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Hora inicio: *
            </label>
            <input
              type="time"
              onChange={(e) =>
                setNewEvent({ ...newEvent, startTime: e.target.value })
              }
            />
          </div>
          <div className="column">
            <label
              style={{
                fontWeight: "bold",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Hora fin: *
            </label>
            <input
              type="time"
              onChange={(e) =>
                setNewEvent({ ...newEvent, endTime: e.target.value })
              }
            />
          </div>
        </div>

        <div className="botones flex gap-10 mt-10">
          <button
            className="boton"
            onClick={handleAddEvent}
            style={{
              backgroundColor: "#007932",
              color: "white",
              border: "2px solid #007932",
              padding: "10px 20px",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Guardar
          </button>
          <button
            className="boton"
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: "12px",
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );

  // Renderizar el modal usando un portal para asegurar que aparezca por encima de todo
  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};

export default ModalAgendarCita;
